import React, { useEffect, useCallback, useContext, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import styles from './orders.module.scss';

import { config } from '../../api/config';
import { actions } from '../../state';
import {
  generateHeader,
  formatDate,
  formatMoney,
  formatTime,
  formatFilename,
  mapServiceTypes,
  mapOrderStatus,
  isFilterActive,
  buildTableFilterBody,
  mapOrderStatusColor,
  mapPaymentMethod,
  paymentMethods,
} from '../../utils';
import {
  Header,
  HeaderActions,
  Title,
  ButtonDownload,
  SelectFilter,
  SelectAsyncFilter,
  RangeFilter,
  DateFilter,
  TimeFilter,
  ButtonSimple,
  Paper,
  Table,
  TableRow,
  TableCell,
} from '../../components';
import { OrdersContext } from './orders.context';
import {
  getOrderFailureReason,
  ORDER_CANCELLATION_REASON_OPTIONS,
  shouldSubmitCancellationReasonFilter,
} from './orders.helpers';

const PAGE_SIZE = config.DEFAULTS.recordsPerPage;
const defaultSortBody = {
  order: [{ by: 'id', order: 'desc' }],
};

const Orders = ({
  dispatch,
  user,
  match: { params, url },
  history,
  location,
  orders: { orders, total, header, footer, loading, orderExport },
  xlsLoading,
  loaction,
}) => {
  const {
    filterBody,
    setFilterBody,
    statusFilter,
    setStatusFilter,
    paymentMethodFilter,
    setPaymentMethodFilter,
    locationFilter,
    setLocationFilter,
    discountFilter,
    setDiscountFilter,
    companyFilter,
    setCompanyFilter,
    companyCodeFilter,
    setCompanyCodeFilter,
    clientFilter,
    setClientFilter,
    businessClientFilter,
    setBusinessClientFilter,
    servicesFilter,
    setServicesFilter,
    costLtFilter,
    setCostLtFilter,
    costGtFilter,
    setCostGtFilter,
    costTaxLtFilter,
    setCostTaxLtFilter,
    costTaxGtFilter,
    setCostTaxGtFilter,
    createdAtLtFilter,
    setCreatedAtLtFilter,
    createdAtGtFilter,
    setCreatedAtGtFilter,
    timeLtFilter,
    setTimeLtFilter,
    timeGtFilter,
    setTimeGtFilter,
    sortOrder,
    setSortOrder,
    sourceFilter,
    setSourceFilter,
    cancellationReasonFilter,
    setCancellationReasonFilter,
  } = useContext(OrdersContext);
  const [page, setPage] = useState(1);
  const pageCount = Math.ceil(total / PAGE_SIZE) || 1;

  const fetchOrders = useCallback(
    data => {
      dispatch(actions.orders.getOrdersAction(data));
    },
    [dispatch]
  );

  const clearOrders = useCallback(() => {
    dispatch(actions.orders.clearOrdersAction());
  }, [dispatch]);

  useEffect(() => () => clearOrders(), [clearOrders]);

  // OrdersProvider is app-scoped, so filterBody/UI filters survive navigation.
  // Reset them when entering a deep-link so the route param owns the query.
  useEffect(() => {
    if (
      !params.client_id &&
      !params.business_client_id &&
      !params.phone &&
      !params.location
    ) {
      return;
    }

    setFilterBody();
    setStatusFilter([]);
    setPaymentMethodFilter([]);
    setLocationFilter([]);
    setDiscountFilter([]);
    setCompanyFilter([]);
    setCompanyCodeFilter([]);
    setClientFilter([]);
    setBusinessClientFilter();
    setServicesFilter([]);
    setSourceFilter([]);
    setCancellationReasonFilter([]);
    setCostLtFilter('');
    setCostGtFilter('');
    setCostTaxLtFilter('');
    setCostTaxGtFilter('');
    setCreatedAtLtFilter();
    setCreatedAtGtFilter();
    setTimeLtFilter('00:00');
    setTimeGtFilter('00:00');
    setSortOrder('desc');
    setPage(1);
  }, [
    params.business_client_id,
    params.client_id,
    params.location,
    params.phone,
    setBusinessClientFilter,
    setCancellationReasonFilter,
    setClientFilter,
    setCompanyCodeFilter,
    setCompanyFilter,
    setCostGtFilter,
    setCostLtFilter,
    setCostTaxGtFilter,
    setCostTaxLtFilter,
    setCreatedAtGtFilter,
    setCreatedAtLtFilter,
    setDiscountFilter,
    setFilterBody,
    setLocationFilter,
    setPaymentMethodFilter,
    setServicesFilter,
    setSortOrder,
    setSourceFilter,
    setStatusFilter,
    setTimeGtFilter,
    setTimeLtFilter,
  ]);

  useEffect(() => {
    const presetOrderFilters =
      location &&
      location.state &&
      typeof location.state === 'object' &&
      location.state.presetOrderFilters;

    if (
      params.location ||
      params.phone ||
      params.business_client_id ||
      params.client_id ||
      !presetOrderFilters
    ) {
      return;
    }

    setStatusFilter(presetOrderFilters.statusFilter || []);
    setPaymentMethodFilter([]);
    setLocationFilter([]);
    setDiscountFilter([]);
    setCompanyFilter([]);
    setCompanyCodeFilter([]);
    setClientFilter([]);
    setBusinessClientFilter();
    setServicesFilter(presetOrderFilters.servicesFilter || []);
    setSourceFilter([]);
    setCancellationReasonFilter(
      presetOrderFilters.cancellationReasonFilter || []
    );

    setCostLtFilter('');
    setCostGtFilter('');
    setCostTaxLtFilter('');
    setCostTaxGtFilter('');

    setCreatedAtGtFilter(presetOrderFilters.createdAtGtFilter);
    setCreatedAtLtFilter(presetOrderFilters.createdAtLtFilter);
    setTimeLtFilter('00:00');
    setTimeGtFilter('00:00');

    setSortOrder(presetOrderFilters.sortOrder || 'desc');
    setFilterBody(presetOrderFilters.filterBody);
    setPage(1);
    history.replace(config.APP_PATHS.orders);
  }, [
    history,
    location,
    params.business_client_id,
    params.client_id,
    params.location,
    params.phone,
    setBusinessClientFilter,
    setClientFilter,
    setCompanyCodeFilter,
    setCompanyFilter,
    setCostGtFilter,
    setCostLtFilter,
    setCostTaxGtFilter,
    setCostTaxLtFilter,
    setCreatedAtGtFilter,
    setCreatedAtLtFilter,
    setDiscountFilter,
    setFilterBody,
    setLocationFilter,
    setPaymentMethodFilter,
    setServicesFilter,
    setSortOrder,
    setSourceFilter,
    setCancellationReasonFilter,
    setStatusFilter,
    setTimeGtFilter,
    setTimeLtFilter,
  ]);

  useEffect(() => {
    if (
      !filterBody &&
      !params.location &&
      !params.phone &&
      !params.business_client_id &&
      !params.client_id &&
      location &&
      location.state &&
      location.state.presetOrderFilters
    ) {
      return;
    }

    let filterToSubmit;
    // Deep-link route params must win over stale filterBody from OrdersProvider.
    if (params.location) {
      filterToSubmit = {
        filter: {
          location_id: params.location,
        },
      };
      const formattedAddress = `${loaction.address}, ${loaction.city}`;
      setLocationFilter([formattedAddress]);
    } else if (params.phone) {
      filterToSubmit = {
        filter: {
          client_phone: [params.phone],
        },
      };
      setClientFilter([params.phone]);
    } else if (params.business_client_id) {
      filterToSubmit = {
        filter: {
          business_client_id: [params.business_client_id],
        },
      };
      setBusinessClientFilter([params.business_client_id]);
    } else if (params.client_id) {
      filterToSubmit = {
        filter: {
          client_id: [params.client_id],
        },
      };
    } else if (filterBody) {
      filterToSubmit = filterBody;
    }
    const offset = (page - 1) * PAGE_SIZE;
    const requestBody =
      filterToSubmit && (filterToSubmit.filter || filterToSubmit.order)
        ? { ...filterToSubmit, offset }
        : { ...defaultSortBody, ...(filterToSubmit || {}), offset };

    fetchOrders(requestBody);
  }, [
    fetchOrders,
    clearOrders,
    filterBody,
    params.phone,
    params.location,
    params.business_client_id,
    params.client_id,
    loaction.address,
    loaction.city,
    location,
    page,
  ]);

  const handleSubmit = submitOptions => {
    if (
      params.location ||
      params.phone ||
      params.business_client_id ||
      params.client_id
    ) {
      history.push(config.APP_PATHS.orders);
    }

    const generatedFilter = [];

    if (statusFilter.length) {
      generatedFilter.push({ field: 'status', value: statusFilter });
    }
    if (shouldSubmitCancellationReasonFilter(cancellationReasonFilter)) {
      generatedFilter.push({
        field: 'cancellation_reason',
        value: cancellationReasonFilter,
      });
    }
    if (paymentMethodFilter.length) {
      generatedFilter.push({
        field: 'payment_method',
        value: paymentMethodFilter,
      });
    }
    if (locationFilter.length) {
      generatedFilter.push({ field: 'location', value: locationFilter });
    }
    if (sourceFilter.length) {
      generatedFilter.push({ field: 'source', value: sourceFilter });
    }
    if (companyFilter.length) {
      generatedFilter.push({ field: 'company_name', value: companyFilter });
    }
    if (companyCodeFilter.length) {
      generatedFilter.push({ field: 'company_code', value: companyCodeFilter });
    }
    if (clientFilter.length) {
      generatedFilter.push({ field: 'client_phone', value: clientFilter });
    }
    if (discountFilter.length) {
      generatedFilter.push({
        field: 'discounts',
        value: discountFilter,
        expression: 'overlap',
        convert: 'text',
      });
    }

    if (costTaxLtFilter > 0) {
      generatedFilter.push({
        field: 'total_cost',
        expression: 'lte',
        value: costTaxLtFilter * 100,
      });
    }
    if (costTaxGtFilter > 0) {
      generatedFilter.push({
        field: 'total_cost',
        expression: 'gte',
        value: costTaxGtFilter * 100,
      });
    }
    if (createdAtGtFilter) {
      generatedFilter.push({
        field: 'created_at',
        expression: 'gte',
        value: createdAtGtFilter,
        type: 'timestamp',
      });
    }
    if (createdAtLtFilter) {
      generatedFilter.push({
        field: 'created_at',
        expression: 'lte',
        value: createdAtLtFilter,
        type: 'timestamp',
      });
    }
    if (timeLtFilter !== '00:00') {
      generatedFilter.push({
        field: 'time',
        expression: 'lte',
        value: timeLtFilter,
        type: 'time',
      });
    }
    if (timeGtFilter !== '00:00') {
      generatedFilter.push({
        field: 'time',
        expression: 'gte',
        value: timeGtFilter,
        type: 'time',
      });
    }
    if (servicesFilter.length) {
      generatedFilter.push({
        field: 'service_types',
        value: servicesFilter,
        convert: 'smallint',
        expression: 'overlap',
      });
    }

    setFilterBody(
      buildTableFilterBody({
        submitOptions,
        generatedFilter,
        sortOrder,
        defaultSortBody,
      })
    );
    setPage(1);
  };

  const clearFilters = () => {
    setStatusFilter([]);
    setPaymentMethodFilter([]);
    setLocationFilter([]);
    setDiscountFilter([]);
    setCompanyFilter([]);
    setCompanyCodeFilter([]);
    setClientFilter([]);
    setBusinessClientFilter([]);
    setServicesFilter([]);
    setSourceFilter([]);
    setCancellationReasonFilter([]);

    setCostLtFilter('');
    setCostGtFilter('');
    setCostTaxLtFilter('');
    setCostTaxGtFilter('');

    setCreatedAtLtFilter();
    setCreatedAtGtFilter();

    setTimeLtFilter('00:00');
    setTimeGtFilter('00:00');

    handleSubmit({ clear: true });
    setPage(1);
    if (url !== config.APP_PATHS.orders) {
      history.push(config.APP_PATHS.orders);
    }
  };

  useEffect(() => {
    if (page > pageCount) {
      setPage(1);
    }
  }, [page, pageCount]);

  const tableHeader = [
    <div className={styles.tableHeader}>Užsakymo ID</div>,
    <SelectFilter
      title="Statusas"
      name="status"
      valueType="number"
      array={generateHeader(header.status, mapOrderStatus)}
      filterState={statusFilter}
      setFilterState={setStatusFilter}
      onSubmit={handleSubmit}
      sortOrder={sortOrder}
      setSortOrder={setSortOrder}
      isActive={isFilterActive('select', statusFilter)}
    />,
    <SelectFilter
      title="Klaida"
      name="cancellation_reason"
      valueType="string"
      array={generateHeader(ORDER_CANCELLATION_REASON_OPTIONS)}
      filterState={cancellationReasonFilter}
      setFilterState={setCancellationReasonFilter}
      onSubmit={handleSubmit}
      sortOrder={sortOrder}
      setSortOrder={setSortOrder}
      isActive={shouldSubmitCancellationReasonFilter(cancellationReasonFilter)}
    />,
    <SelectFilter
      title="Mokėjimo metodas"
      name="payment_method"
      valueType="number"
      array={generateHeader(paymentMethods, mapPaymentMethod)}
      filterState={paymentMethodFilter}
      setFilterState={setPaymentMethodFilter}
      onSubmit={handleSubmit}
      sortOrder={sortOrder}
      setSortOrder={setSortOrder}
      isActive={isFilterActive('select', paymentMethodFilter)}
    />,
    <SelectFilter
      title="Lokacija"
      name="location"
      valueType="string"
      array={generateHeader(header.location)}
      filterState={locationFilter}
      setFilterState={setLocationFilter}
      onSubmit={handleSubmit}
      sortOrder={sortOrder}
      setSortOrder={setSortOrder}
      isActive={isFilterActive('select', locationFilter)}
    />,
    <DateFilter
      title="Data"
      name="created_at"
      lt={createdAtLtFilter}
      setLt={setCreatedAtLtFilter}
      gt={createdAtGtFilter}
      setGt={setCreatedAtGtFilter}
      onSubmit={handleSubmit}
      sortOrder={sortOrder}
      setSortOrder={setSortOrder}
      isActive={isFilterActive('date', createdAtLtFilter, createdAtGtFilter)}
    />,
    <TimeFilter
      title="Laikas"
      name="time"
      lt={timeLtFilter}
      setLt={setTimeLtFilter}
      gt={timeGtFilter}
      setGt={setTimeGtFilter}
      onSubmit={handleSubmit}
      sortOrder={sortOrder}
      setSortOrder={setSortOrder}
      isActive={isFilterActive('time', timeLtFilter, timeGtFilter)}
    />,
    <SelectAsyncFilter
      title="Įmonė"
      name="company_name"
      valueType="string"
      params={{ model: 'companies', field: 'company_name' }}
      filterState={companyFilter}
      setFilterState={setCompanyFilter}
      onSubmit={handleSubmit}
      sortOrder={sortOrder}
      setSortOrder={setSortOrder}
      isActive={isFilterActive('select', companyFilter)}
    />,
    <SelectAsyncFilter
      title="Įmonės kodas"
      name="company_code"
      valueType="string"
      params={{ model: 'companies', field: 'company_code' }}
      filterState={companyCodeFilter}
      setFilterState={setCompanyCodeFilter}
      onSubmit={handleSubmit}
      sortOrder={sortOrder}
      setSortOrder={setSortOrder}
      isActive={isFilterActive('select', companyCodeFilter)}
    />,
    <SelectFilter
      title="Nuolaida"
      name="discounts"
      valueType="string"
      array={generateHeader(header.discounts)}
      filterState={discountFilter}
      setFilterState={setDiscountFilter}
      onSubmit={handleSubmit}
      sortOrder={sortOrder}
      setSortOrder={setSortOrder}
      isActive={isFilterActive('select', discountFilter)}
    />,
    <SelectAsyncFilter
      title="Numeris"
      name="client_phone"
      valueType="string"
      params={{ model: 'clients', field: 'phone' }}
      filterState={clientFilter}
      setFilterState={setClientFilter}
      onSubmit={handleSubmit}
      sortOrder={sortOrder}
      setSortOrder={setSortOrder}
      isActive={isFilterActive('select', clientFilter)}
    />,
    <RangeFilter
      title="Suma su PVM"
      name="total_cost"
      lt={costTaxLtFilter}
      setLt={setCostTaxLtFilter}
      gt={costTaxGtFilter}
      setGt={setCostTaxGtFilter}
      onSubmit={handleSubmit}
      sortOrder={sortOrder}
      setSortOrder={setSortOrder}
      position="right"
      isActive={isFilterActive('range', costTaxLtFilter, costTaxGtFilter)}
    />,
    <SelectFilter
      title="Paslaugos"
      name="service_types"
      valueType="number"
      array={generateHeader(header.service_types, mapServiceTypes)}
      filterState={servicesFilter}
      setFilterState={setServicesFilter}
      onSubmit={handleSubmit}
      sortOrder={sortOrder}
      setSortOrder={setSortOrder}
      position="right"
      isActive={isFilterActive('select', servicesFilter)}
    />,
    <SelectFilter
      title="Šaltinis"
      name="source"
      valueType="string"
      array={generateHeader(header.source)}
      filterState={sourceFilter}
      setFilterState={setSourceFilter}
      onSubmit={handleSubmit}
      sortOrder={sortOrder}
      setSortOrder={setSortOrder}
      isActive={isFilterActive('select', sourceFilter)}
    />,
    <div className={styles.tableHeader}>Išorinis ID</div>,
    <div className={styles.tableHeader}>Sutrikimas</div>,
  ];

  const renderTypes = types =>
    types.map(type => <p key={type}>{mapServiceTypes(type)}</p>);

  const renderTable = () =>
    orders.map(
      ({
        id,
        status,
        cancellation_reason,
        payment_method,
        payment_card_type,
        location,
        created_at,
        time,
        company_name,
        company_code,
        discounts,
        client_phone,
        total_cost,
        service_types,
        source,
        external_id,
        failure_reason,
        error_message,
      }) => (
        <TableRow url={config.APP_PATHS.orderDetails(id)} key={id}>
          <TableCell>{id}</TableCell>
          <TableCell>
            <span style={{ color: mapOrderStatusColor(status) }}>
              {mapOrderStatus(status)}
            </span>
          </TableCell>
          <TableCell>
            {getOrderFailureReason({ cancellation_reason })}
          </TableCell>
          <TableCell>
            {mapPaymentMethod(payment_method, payment_card_type)}
          </TableCell>
          <TableCell>{location}</TableCell>
          <TableCell>{formatDate(created_at)}</TableCell>
          <TableCell>{formatTime(time)}</TableCell>
          <TableCell>{company_name}</TableCell>
          <TableCell>{company_code}</TableCell>
          <TableCell>
            {discounts.map((discount, i) => (
              <p key={i}>{discount}</p>
            ))}
          </TableCell>
          <TableCell>{client_phone}</TableCell>
          <TableCell align="right">{formatMoney(total_cost)}</TableCell>
          <TableCell>{renderTypes(service_types)}</TableCell>
          <TableCell>{source}</TableCell>
          <TableCell>{external_id || '-'}</TableCell>
          <TableCell>
            {getOrderFailureReason({ status, failure_reason, error_message })}
          </TableCell>
        </TableRow>
      )
    );

  const xlsExport = () => {
    const filename = formatFilename(config.PAGES.orders);
    //dispatch(actions.orders.exportOrdersAction(filename, filterBody));
    dispatch(actions.orders.exportOrdersAsyncAction(filterBody, filename));
  };

  const hasActiveFilters =
    !!filterBody ||
    !!params.location ||
    !!params.phone ||
    !!params.business_client_id ||
    !!params.client_id ||
    !!statusFilter.length ||
    !!paymentMethodFilter.length ||
    !!locationFilter.length ||
    !!discountFilter.length ||
    !!companyFilter.length ||
    !!companyCodeFilter.length ||
    !!clientFilter.length ||
    !!(businessClientFilter && businessClientFilter.length) ||
    !!servicesFilter.length ||
    !!sourceFilter.length ||
    shouldSubmitCancellationReasonFilter(cancellationReasonFilter) ||
    !!createdAtLtFilter ||
    !!createdAtGtFilter ||
    timeLtFilter !== '00:00' ||
    timeGtFilter !== '00:00' ||
    Number(costTaxLtFilter) > 0 ||
    Number(costTaxGtFilter) > 0;

  return (
    <section>
      <Header>
        <Title total={total}>{config.PAGES.orders}</Title>
        <HeaderActions>
          {hasActiveFilters && (
            <ButtonSimple type="filter" onClick={clearFilters}>
              Išvalyti filtrus
            </ButtonSimple>
          )}
          <ButtonDownload onClick={xlsExport} loading={xlsLoading} />
        </HeaderActions>
      </Header>

      <Paper className={styles.totalPaper}>
        <div className={styles.totalPaperRow}>
          <span className={styles.totalPaperLabel}>
            Suma su PVM ({orders.length} įrašų)
          </span>
          <span className={styles.totalPaperAmount}>
            {formatMoney(footer.total_cost)}
          </span>
        </div>
      </Paper>

      <Table
        header={tableHeader}
        filter={true}
        isEmpty={!orders.length}
        loading={loading}
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        serverPagination={true}
        onPageChange={setPage}
      >
        {renderTable()}
      </Table>
    </section>
  );
};

const mapStateToProps = state => ({
  orders: state.orders,
  user: state.auth.user,
  xlsLoading: state.download.xlsLoading,
  loaction: state.locations.selectedLocation,
});

Orders.propTypes = {
  dispatch: PropTypes.func.isRequired,
  match: PropTypes.shape({
    params: PropTypes.object.isRequired,
    url: PropTypes.string.isRequired,
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
    replace: PropTypes.func.isRequired,
  }).isRequired,
  location: PropTypes.shape({
    state: PropTypes.object,
  }),
  orders: PropTypes.shape({
    orders: PropTypes.array.isRequired,
    total: PropTypes.number.isRequired,
    header: PropTypes.shape({
      status: PropTypes.arrayOf(PropTypes.number),
      location: PropTypes.arrayOf(PropTypes.string),
      payment_type: PropTypes.arrayOf(PropTypes.number),
      company_name: PropTypes.arrayOf(PropTypes.string),
      company_code: PropTypes.arrayOf(PropTypes.string),
      discounts: PropTypes.arrayOf(PropTypes.string),
      client_phone: PropTypes.arrayOf(PropTypes.string),
      service_types: PropTypes.arrayOf(PropTypes.number),
    }).isRequired,
    footer: PropTypes.shape({
      total_cost: PropTypes.number,
    }).isRequired,
    loading: PropTypes.bool.isRequired,
  }).isRequired,
  xlsLoading: PropTypes.bool.isRequired,
};

export default connect(mapStateToProps)(Orders);
